import { TempActions } from '@mode/charts/domain';
import { AppState, ClientState } from '@mode/legacy/types-app-state';
import { ReducerTree, combineReducers } from '@mode/legacy/util-domain';
import {
  DefinitionActionsReducer,
  SelectedDefinitionReducer,
  initialDefinitionActionsState,
  initialSelectedDefinitionState,
} from '~/modules/definitions/domain';
import { SharedChartCommandActionType } from '../actions/chart-commands/shared.actions';
import { ExplorerActionType } from '../actions/chart-designer/explorer.actions';
import { ClientActionType } from '../actions/client.actions';
import { Actions } from '../actions/index';
import { RouterActionType } from '../actions/router-actions';
import { cellRunResultsReducer, initialCellRunResultsState } from './client/notebooks/cell-run-results.reducers';
import { cellRunsReducer, initialCellRunsState } from './client/notebooks/cell-runs.reducers';
import { cellsReducer, initialCellsState } from './client/notebooks/cells.reducers';
import { initialNotebookEditorState, notebookEditorReducer } from './client/notebooks/notebook-editor.reducers';
import { initialNotebooksState, notebooksReducer } from './client/notebooks/notebook.reducers';
import { ReportContextAvatarReducer, initialOrgAvatarState } from './client/org-context-avatar.reducers';
import { SelectedReportReducer, initialSelectedReportState } from './client/selected-report.reducers';

export const initialClientState: ClientState = {
  loggedIn: false,
  currentReportToken: null,
  currentAccountUsername: null,
  currentUserBelongsToReportContext: null,
  account: null,
  accountOrganizations: null,
  accountPreference: null,
  accountContext: null,
  accountContextSpaces: null,
  accountContextDatasources: null,
  accountContextFeatures: null,
  definitionActions: initialDefinitionActionsState,
  selectedReport: initialSelectedReportState,
  selectedDefinition: initialSelectedDefinitionState,
  reportContext: null,
  reportContextAvatar: initialOrgAvatarState,
  reportContextDatasources: null,
  reportContextFeatures: null,
  expandedViewOpen: false,

  notebooks: {
    books: initialNotebooksState,
    cells: initialCellsState,
    cellRuns: initialCellRunsState,
    cellRunResults: initialCellRunResultsState,
    editor: initialNotebookEditorState,
  },
};

// Client State
function loggedInReducer(state = initialClientState.loggedIn, action: Actions): ClientState['loggedIn'] {
  switch (action.type) {
    case ClientActionType.LoadLoggedInState:
      return action.loggedIn;
  }

  return state;
}

function currentReportTokenReducer(
  state = initialClientState.currentReportToken,
  action: Actions
): ClientState['currentReportToken'] {
  switch (action.type) {
    case RouterActionType.UpdateRoute:
      return action.payload.params.reportId;
  }

  return state;
}

function currentAccountUsernameReducer(
  state = initialClientState.currentAccountUsername,
  action: Actions
): ClientState['currentAccountUsername'] {
  switch (action.type) {
    case RouterActionType.UpdateRoute:
      return action.payload.params.ownerId;
  }

  return state;
}

function currentUserBelongsToReportContextReducer(
  state = initialClientState.currentUserBelongsToReportContext,
  action: Actions
): ClientState['currentUserBelongsToReportContext'] {
  switch (action.type) {
    case ClientActionType.ComputeIfUserBelongsToReportContext:
      return action.result;
  }

  return state;
}

function accountReducer(state = initialClientState.account, action: Actions): ClientState['account'] {
  switch (action.type) {
    case ClientActionType.LoadAccountSuccess:
      return action.payload.account;
  }

  return state;
}

function accountOrganizationsReducer(
  state = initialClientState.accountOrganizations,
  action: Actions
): ClientState['accountOrganizations'] {
  switch (action.type) {
    case ClientActionType.LoadAccountOrganizationsSuccess:
      return action.organizations;
  }

  return state;
}

function accountPreferenceReducer(
  state = initialClientState.accountPreference,
  action: Actions
): ClientState['accountPreference'] {
  switch (action.type) {
    case ClientActionType.LoadAccountPreferenceSuccess:
      return action.preferenceResource;
    case ClientActionType.UpdatePreferenceSuccess:
      return state.generate(action.preferenceResource);
  }

  return state;
}

function accountContextReducer(
  state = initialClientState.accountContext,
  action: Actions
): ClientState['accountContext'] {
  switch (action.type) {
    case ClientActionType.LoadAccountContextSuccess:
      return action.organization;
  }

  return state;
}

function accountContextDatasourcesReducer(
  state = initialClientState.accountContextDatasources,
  action: Actions
): ClientState['accountContextDatasources'] {
  switch (action.type) {
    case ClientActionType.LoadAccountContextDatasourcesSuccess:
      return action.datasources;
  }

  return state;
}

function accountContextFeaturesReducer(
  state = initialClientState.accountContextFeatures,
  action: Actions
): ClientState['accountContextFeatures'] {
  switch (action.type) {
    case ClientActionType.LoadAccountContextFeaturesSuccess:
      return action.features;
  }

  return state;
}

function accountContextSpacesReducer(
  state = initialClientState.accountContextSpaces,
  action: Actions
): ClientState['accountContextSpaces'] {
  switch (action.type) {
    case ClientActionType.LoadAccountContextSpacesSuccess:
      return action.spaces;
  }

  return state;
}

function reportContextReducer(state = initialClientState.reportContext, action: Actions): ClientState['reportContext'] {
  switch (action.type) {
    case ClientActionType.LoadReportContext:
      return action.organization;
  }

  return state;
}

export function reportContextDatasourcesReducer(
  state = initialClientState.reportContextDatasources,
  action: Actions
): ClientState['reportContextDatasources'] {
  switch (action.type) {
    case ClientActionType.LoadReportContextDatasourcesSuccess:
      return action.datasources;
  }

  return state;
}

function reportContextFeaturesReducer(
  state = initialClientState.reportContextFeatures,
  action: Actions
): ClientState['reportContextFeatures'] {
  switch (action.type) {
    case ClientActionType.LoadReportContextFeaturesSuccess:
      return action.features;
  }

  return state;
}

function expandedViewReducer(
  state = initialClientState.expandedViewOpen,
  action: Actions
): ClientState['expandedViewOpen'] {
  switch (action.type) {
    case TempActions.Type.ExploreChartMark:
      return true;
    case SharedChartCommandActionType.CloseAllModals: // fallthrough
    case ExplorerActionType.CloseExploration:
      return false;
  }

  return state;
}

export const ClientReducer: ReducerTree<AppState, ClientState, Actions> = {
  loggedIn: loggedInReducer,
  currentReportToken: currentReportTokenReducer,
  currentAccountUsername: currentAccountUsernameReducer,
  currentUserBelongsToReportContext: currentUserBelongsToReportContextReducer,
  account: accountReducer,
  accountOrganizations: accountOrganizationsReducer,
  accountPreference: accountPreferenceReducer,
  accountContext: accountContextReducer,
  accountContextSpaces: accountContextSpacesReducer,
  accountContextDatasources: accountContextDatasourcesReducer,
  accountContextFeatures: accountContextFeaturesReducer,
  definitionActions: DefinitionActionsReducer,
  selectedReport: SelectedReportReducer,
  selectedDefinition: SelectedDefinitionReducer,
  reportContext: reportContextReducer,
  reportContextAvatar: ReportContextAvatarReducer,
  reportContextDatasources: reportContextDatasourcesReducer,
  reportContextFeatures: reportContextFeaturesReducer,
  expandedViewOpen: expandedViewReducer,

  notebooks: {
    books: notebooksReducer,
    cells: cellsReducer,
    cellRuns: cellRunsReducer,
    cellRunResults: cellRunResultsReducer,
    editor: notebookEditorReducer,
  },
};

export function clientReducer(state: ClientState, action: Actions) {
  return combineReducers(ClientReducer)(state, action);
}
